import { useEffect, useRef } from 'react';
import type { StoreApi, UseBoundStore } from 'zustand';
import type { DocumentStoreState } from '@/stores/createDocumentStore';
import { useUiStore } from '@/stores/uiStore';

interface AutosaveOptions<T> {
  store: UseBoundStore<StoreApi<DocumentStoreState<T>>>;
  save: (input: { id: string; version: number; patch: Partial<T> }) => Promise<{ version: number }>;
  enabled: boolean;
  debounceMs?: number;
}

/**
 * Debounces edits to a document store into a PATCH, keyed off the store's own
 * `saveState`/`document` rather than a dependency array, so it reacts to
 * every editor that calls `.patch()` regardless of which panel it lives in.
 * A 3s debounce matches the plan's autosave cadence.
 *
 * `save`/`enabled`/`debounceMs` are read through refs rather than sitting in
 * the main effect's dependency array: `save` here is `useMutation(...).mutateAsync`
 * wrapped in a fresh arrow function on every render of the calling component,
 * so depending on it directly tore the subscription down and rebuilt it —
 * silently dropping any pending debounce timer — on every unrelated
 * re-render. The subscription is rebuilt only when the store or enabled state
 * changes; re-enabling autosave also schedules any edits accumulated while it
 * was disabled.
 */
export function useAutosave<T extends { id: string }>({
  store,
  save,
  enabled,
  debounceMs = 3000,
}: AutosaveOptions<T>): void {
  const timerRef = useRef<number | null>(null);
  const saveRef = useRef(save);
  const enabledRef = useRef(enabled);
  const debounceMsRef = useRef(debounceMs);
  const saveInFlightRef = useRef<Promise<void> | null>(null);

  saveRef.current = save;
  enabledRef.current = enabled;
  debounceMsRef.current = debounceMs;

  useEffect(() => {
    async function flush() {
      if (!enabledRef.current) return;

      // A slow request must not let a second request leave with the same
      // optimistic-concurrency version. Wait for it to reconcile first.
      if (saveInFlightRef.current) await saveInFlightRef.current;

      const { id, version, document } = store.getState();
      if (!enabledRef.current || !id || !document || store.getState().saveState !== 'dirty') return;

      store.getState().setSaveState('saving');
      const request = (async () => {
        try {
          const { id: _id, ...patch } = document as T & { id: string };
          const result = await saveRef.current({ id, version, patch: patch as Partial<T> });

          // Loading another document while this request is in flight must not
          // apply its version to the newly opened document.
          if (store.getState().id === id) store.getState().markSaved(result.version, document);
        } catch (error) {
          const latest = store.getState();
          if (latest.id !== id) return;
          const message = error instanceof Error ? error.message : 'Save failed';
          latest.setSaveState(latest.document === document ? 'error' : 'dirty', message);
          useUiStore.getState().notify({ kind: 'danger', title: 'Autosave failed', message });
        }
      })();
      saveInFlightRef.current = request;
      await request;
      if (saveInFlightRef.current === request) saveInFlightRef.current = null;
    }

    const unsubscribe = store.subscribe((state, prevState) => {
      // Any document transition invalidates the old debounce. In particular,
      // opening another document must never cause the old timer to save the
      // newly opened (and otherwise clean) document.
      if (state.id !== prevState.id || state.document !== prevState.document) {
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (
        !enabledRef.current ||
        state.saveState !== 'dirty' ||
        state.document === prevState.document
      )
        return;

      timerRef.current = window.setTimeout(() => void flush(), debounceMsRef.current);
    });

    // Edits are allowed while autosave is disabled. When it is switched back
    // on there may be no subsequent store update to wake the subscriber, so
    // explicitly schedule the already-dirty document here.
    if (enabledRef.current && store.getState().saveState === 'dirty') {
      timerRef.current = window.setTimeout(() => void flush(), debounceMsRef.current);
    }

    return () => {
      unsubscribe();
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [enabled, store]);
}
